"use client";

/**
 * EoaSweepBanner — floating bottom-right banner that surfaces when a user
 * has USDC/pUSD stranded on their Privy EOA (login wallet) instead of
 * their deposit wallet (trading wallet). One-click move via useEoaSweep
 * (shared with the inline card in /settings?tab=wallet).
 *
 * Funds can't be lost: destination is the user's own deposit wallet
 * (deterministic CREATE2 of EOA + factory, persisted in DB and verified
 * pre-sign). On any failure, balances stay safe on the EOA.
 */

import { useState } from "react";
import { useEoaSweep } from "@/hooks/useEoaSweep";

export default function EoaSweepBanner() {
  const { needsMove, balances, depositWallet, eoa, running, swept, move } = useEoaSweep();
  const [dismissed, setDismissed] = useState(false);

  // `swept` hides the banner the instant a successful move clears every
  // token, before the on-chain balance / cache catches up.
  if (!needsMove || swept || dismissed || !balances || !depositWallet || !eoa) return null;

  const total = balances.totalUsd;

  // Floating bottom-right card. Earlier iterations: (1) sticky top-0 →
  // covered the markets cat-bar; (2) inline → hidden behind the
  // page-level sticky source tabs. Detaching it from document flow as a
  // fixed card eliminates both collisions and matches the standard
  // "update available / fund detected" pattern. Anchored bottom-right
  // so it doesn't fight the bottom-left dev toolbar or the mobile
  // bottom nav (which sits at bottom-center on small screens).
  return (
    <div className="fixed right-4 bottom-[calc(52px+env(safe-area-inset-bottom)+0.75rem)] lg:bottom-4 z-[300] w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px]">
      <div className="bg-[#18181D] border border-[#2A2A30] rounded-[8px] shadow-2xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <span className="relative flex h-2 w-2 flex-shrink-0 mt-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#CCFF00] opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#CCFF00]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-200 leading-snug">
              <span className="font-semibold text-white">${total.toFixed(2)}</span>{" "}
              detected on your login wallet — move it to your trading wallet to start trading.
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="text-gray-500 hover:text-white text-lg leading-none -mt-1 flex-shrink-0"
          >
            ×
          </button>
        </div>
        <button
          onClick={move}
          disabled={running}
          className="w-full px-4 py-2 rounded-[5px] bg-[#CCFF00] text-[#0f0f12] text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#b8e600] transition-colors"
        >
          {running ? "Moving..." : "Move to deposit →"}
        </button>
      </div>
    </div>
  );
}
